import React from "react";

export default function MinimalHomepage() {
  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur border-b border-slate-200">
        <div className="mx-auto max-w-6xl px-4 py-3 flex items-center justify-between">
          <a href="#" className="font-semibold tracking-tight">DeepDocs AI</a>
          <nav className="hidden md:flex items-center gap-6 text-sm">
            <a href="/" className="hover:opacity-70">Home</a>
            <a href="#ai-factories" className="hover:opacity-70">AI Factories</a>
            <a href="#features" className="hover:opacity-70">Features</a>
            <a href="#clients" className="hover:opacity-70">Clients</a>
          </nav>
          <div className="flex items-center gap-3">
            <a href="#book-demo" className="inline-flex items-center rounded-full border border-slate-300 px-4 py-2 text-sm hover:bg-slate-50">Learn more</a>
            <a href="#book-demo" className="inline-flex items-center rounded-full bg-slate-900 px-4 py-2 text-sm text-white hover:bg-black">Get demo</a>
          </div>
        </div>
      </header>

      <main>
        {/* Hero Section */}
        <section className="mx-auto max-w-6xl px-4 py-20" id="hero">
          <div className="grid gap-10 md:grid-cols-2 md:items-center">
            <div>
              <p className="text-sm uppercase tracking-widest text-slate-500"># DeepDocs AI Homepage - Minimal Version</p>
              <h1 className="mt-4 text-4xl md:text-6xl font-semibold leading-tight">Go faster. Think deeper. Do better.</h1>
              <p className="mt-6 text-xl md:text-2xl text-slate-700">A High Precision Framework for Healthcare & Life Sciences</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href="#book-demo" className="inline-flex items-center rounded-full bg-slate-900 px-5 py-3 text-sm font-medium text-white hover:bg-black">Get demo</a>
                <a href="#ai-factories" className="inline-flex items-center rounded-full border border-slate-300 px-5 py-3 text-sm font-medium hover:bg-slate-50">Learn more</a>
              </div>
            </div>
            <div className="relative">
              <VisualSlot title="Hero visual" ratio="16/10" className="md:ml-auto" />
            </div>
          </div>
        </section>

        <hr className="border-slate-200" />

        {/* Stats Section */}
        <section className="mx-auto max-w-6xl px-4 py-14" aria-label="Stats Section">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <Stat k="0%" v="Hallucination rate" />
            <Stat k="100%" v="Data privacy" />
            <Stat k="25+" v="Languages supported" />
            <Stat k="150+" v="Clinical experts using" />
          </div>
        </section>

        <hr className="border-slate-200" />

        {/* AI Factories Section */}
        {/* <section id="ai-factories" className="mx-auto max-w-6xl px-4 py-16">
          <header className="mb-10">
            <h2 className="text-2xl font-semibold">Purpose-built AI Factories for Expert Teams</h2>
            <p className="mt-2 text-slate-700">Stop using generic AI tools. Get specialized workflows designed for regulatory compliance.</p>
          </header>

          <div className="grid gap-8 md:grid-cols-2">
            <Factory
              title="Clinical Affairs Factory"
              subtitle="Literature analysis, evidence synthesis, and clinical evaluation"
              bullets={[
                'Dam Select - Automated study selection',
                'Dam Assess - Quality assessment',
                'Dam Extract - Data extraction',
                'DeepWrite SR - Report generation',
              ]}
            />
            <Factory
              title="Clinical Investigation Factory"
              subtitle="Clinical study reports, protocol analysis, and data extraction"
              bullets={[
                'Dam Extract - Multi-format data extraction',
                'DeepTemplate - Protocol compliance check',
              ]}
            />
          </div>
        </section> */}

        <hr className="border-slate-200" />

        {/* Features Section */}
        <section id="features" className="mx-auto max-w-6xl px-4 py-16">
          <h2 className="text-2xl font-semibold mb-8">Built for clinical excellence</h2>
          <div className="grid gap-6 md:grid-cols-2">
            <Feature
              title="Document intelligence"
              body="Purpose-built for medical literature, clinical evaluation reports, and regulatory submissions with complete audit trails."
            />
            <Feature
              title="Privacy by design"
              body="Your clinical data never leaves your infrastructure. Deploy on-premise, in private clouds, or air-gapped environments."
            />
            <Feature
              title="Global compliance"
              body="Process regulatory documents in 25+ languages with understanding of cultural nuances and local regulations."
            />
            <Feature
              title="Observable workflows"
              body="Complete transparency in every processing step. Real-time monitoring and full traceability for compliance requirements."
            />
          </div>
        </section>

        <hr className="border-slate-200" />

        {/* Call-to-Action Section */}
        <section id="book-demo" className="mx-auto max-w-6xl px-4 py-20 text-center">
          <h2 className="text-2xl md:text-3xl font-semibold">Validated by the industry</h2>
          <p className="mt-3 text-slate-700">See how we're accelerating clinical operations for medical device, pharma & CRO teams worldwide</p>
          <a href="#" className="mt-6 inline-flex items-center rounded-full bg-slate-900 px-6 py-3 text-sm font-medium text-white hover:bg-black">Book your demo slot this week</a>
          <p className="mt-4 text-sm text-slate-600">See your document type processed live • Get ROI calculation • Your data stays private</p>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-6xl px-4 py-16 grid gap-10 md:grid-cols-4">
          <div className="md:col-span-2">
            <h3 className="font-semibold">DeepDocs AI</h3>
            <p className="mt-3 text-sm text-slate-700">The only AI platform built for clinical affairs compliance. Write systematic reviews in days, not months.</p>
            <a href="#book-demo" className="mt-4 inline-flex items-center rounded-full bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-black">Book demo</a>
          </div>

          <div>
            <h4 className="font-semibold">Solutions</h4>
            <ul className="mt-3 space-y-2 text-sm text-slate-700">
              <li>Clinical affairs</li>
              <li>Clinical investigation</li>
              <li>Regulatory affairs</li>
              <li>Systematic reviews</li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold">Resources</h4>
            <ul className="mt-3 space-y-2 text-sm text-slate-700">
              <li>Knowledge base</li>
              <li>Free SR guide</li>
              <li>Templates</li>
              <li>Publications</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-200">
          <div className="mx-auto max-w-6xl px-4 py-6 text-xs text-slate-600 text-center">
            <p>© 2025 DeepDocs AI. All rights reserved. Built with revenue, validated by the industry.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Stat({ k, v }) {
  return (
    <div className="rounded-2xl border border-slate-200 p-6">
      <div className="text-3xl font-semibold">{k}</div>
      <div className="mt-1 text-sm text-slate-600">{v}</div>
    </div>
  );
}

function Factory({ title, subtitle, bullets }) {
  return (
    <div className="rounded-2xl border border-slate-200 p-6">
      <h3 className="text-lg font-semibold">{title}</h3>
      <p className="mt-1 text-slate-700">{subtitle}</p>
      <ul className="mt-4 space-y-2 text-sm text-slate-700 list-disc pl-5">
        {bullets.map((b, i) => (
          <li key={i}>{b}</li>
        ))}
      </ul>
    </div>
  );
}

function Feature({ title, body }) {
  return (
    <div className="rounded-2xl border border-slate-200 p-6">
      <h3 className="text-base font-semibold">{title}</h3>
      <p className="mt-2 text-sm text-slate-700">{body}</p>
    </div>
  );
}

function VisualSlot({ title, ratio = "4/3", className = "" }) {
  return (
    <div
      role="img"
      aria-label={title || "visual placeholder"}
      className={`relative w-full rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 ${className}`}
      style={{ aspectRatio: ratio }}
    >
      <div className="absolute inset-0 rounded-2xl" aria-hidden />
    </div>
  );
}
